/**
 * Portal type determines how entities can travel through it.
 */
export type PortalType = 'bidirectional' | 'exit' | 'entrance';

/**
 * A portal connects two game layers, allowing entities to transfer between them.
 */
export interface Portal {
  /** Unique identifier for this portal */
  id: string;

  /** Layer that owns this portal */
  layerId: string;

  /** Layer this portal connects to */
  targetLayerId: string;

  /** Portal on the target layer to receive entities */
  targetPortalId: string;

  /**
   * Position within the owning layer's coordinate system.
   *
   * The *layer's*, not the screen's: the surface's entrances are grid cells and
   * the colony's exits are points in the burrow the ants walk through. Neither
   * is a pixel. The surface's used to be, which meant a headless district
   * projected three portals through a camera nobody was looking through (ADR-009).
   */
  position: { x: number; y: number };

  /**
   * Portal type:
   * - 'bidirectional': Entities can enter and exit through this portal
   * - 'exit': Entities can only leave through this portal (e.g., colony entrance)
   * - 'entrance': Entities can only enter through this portal
   */
  type: PortalType;

  /** Types of entities that can pass through (e.g., ['ant'], ['car', 'person']) */
  entityTypes: string[];

  /** Optional: visual radius for rendering the portal */
  radius?: number;

  /** Optional: whether the portal is currently active */
  active?: boolean;
}

/**
 * An entity that can be transferred between layers through a portal.
 */
export interface TransferableEntity {
  /** Entity type identifier (e.g., 'ant', 'car') */
  type: string;

  /** Serialized entity state - structure depends on entity type */
  data: unknown;

  /** The portal that the entity came from */
  sourcePortal: string;

  /** The layer the entity came from */
  sourceLayer: string;

  /** Timestamp when the transfer was initiated */
  timestamp: number;
}

/**
 * Result of a portal transfer attempt.
 */
export interface PortalTransferResult {
  success: boolean;
  error?: string;
  entity?: TransferableEntity;
}

/**
 * Utility class for creating portal configurations.
 */
export class PortalBuilder {
  private portal: Partial<Portal> = {
    active: true,
  };

  static create(id: string): PortalBuilder {
    const builder = new PortalBuilder();
    builder.portal.id = id;
    return builder;
  }

  inLayer(layerId: string): PortalBuilder {
    this.portal.layerId = layerId;
    return this;
  }

  connectsTo(targetLayerId: string, targetPortalId: string): PortalBuilder {
    this.portal.targetLayerId = targetLayerId;
    this.portal.targetPortalId = targetPortalId;
    return this;
  }

  at(x: number, y: number): PortalBuilder {
    this.portal.position = { x, y };
    return this;
  }

  ofType(type: PortalType): PortalBuilder {
    this.portal.type = type;
    return this;
  }

  allowsEntities(...types: string[]): PortalBuilder {
    this.portal.entityTypes = types;
    return this;
  }

  withRadius(radius: number): PortalBuilder {
    this.portal.radius = radius;
    return this;
  }

  build(): Portal {
    if (!this.portal.id || !this.portal.layerId || !this.portal.targetLayerId ||
        !this.portal.targetPortalId || !this.portal.position || !this.portal.type ||
        !this.portal.entityTypes) {
      throw new Error('Portal configuration is incomplete');
    }

    return this.portal as Portal;
  }
}

/**
 * Serializable representation of an ant for transfer between layers.
 */
export interface AntTransferData {
  id: number;
  name: string;
  customName: string | null;
  role: string;
  age: number;
  health: number;
  size: number;
  isPhilosopher: boolean;
  scarred: boolean;
  stats: {
    foragingTrips: number;
    foodGathered: number;
    distanceTraveled: number;
    threatsRepelled: number;
    eggsNursed: number;
    tunnelsDug: number;
  };
}
